"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.normalizeAttributeName = exports.createLevel = exports.createAttribute = exports.DimensionalLevelAttribute = exports.DimensionalAttribute = exports.jaqlSimpleColumnType = void 0;
const types_js_1 = require("./types.js");
const base_js_1 = require("./base.js");
const simple_column_types_js_1 = require("./simple-column-types.js");
/**
 * @internal
 */
const jaqlSimpleColumnType = (datatype) => (0, simple_column_types_js_1.simpleColumnType)(datatype).replace('number', 'numeric');
exports.jaqlSimpleColumnType = jaqlSimpleColumnType;
/**
 * @internal
 */
class DimensionalAttribute extends base_js_1.DimensionalElement {
    constructor(name, expression, type, desc, sort, dataSource, composeCode, panel) {
        super(name, type || types_js_1.MetadataTypes.Attribute, desc, dataSource, composeCode);
        this._sort = types_js_1.Sort.None;
        this.expression = expression;
        // panel is not needed in most cases, this is to support break by columns functionality
        if (panel === 'columns') {
            this.panel = panel;
        }
        this._sort = sort || types_js_1.Sort.None;
    }
    /**
     * gets the element's ID
     */
    get id() {
        return this.expression;
    }
    /**
     * Gets the sort definition of this instance
     *
     * @returns The Sort definition of this instance
     */
    getSort() {
        return this._sort;
    }
    /**
     * Gets a sorted {@link Attribute} with the given definition
     *
     * @param sort - Sort definition
     * @returns An instance representing the sorted {@link Attribute} of this instance
     */
    sort(sort) {
        return new DimensionalAttribute(this.name, this.expression, this.type, this.description, sort, this.dataSource, this.composeCode, this.panel);
    }
    /**
     * Gets the JAQL representation of this instance
     *
     * @param nested - defines whether the JAQL is nested within parent JAQL statement or a root JAQL element
     */
    jaql(nested) {
        const result = {
            jaql: {
                title: this.name,
                dim: this.expression,
                datatype: (0, exports.jaqlSimpleColumnType)(this.type),
            },
        };
        if (this.panel) {
            result.panel = this.panel;
        }
        if (this._sort != types_js_1.Sort.None) {
            result.jaql.sort = this._sort == types_js_1.Sort.Ascending ? 'asc' : 'desc';
        }
        return nested === true ? result.jaql : result;
    }
    /**
     * Gets a serializable representation of the element
     */
    serializable() {
        const result = super.serializable();
        result.expression = this.expression;
        if (this.getSort() !== types_js_1.Sort.None) {
            result.sort = this.getSort().toString();
        }
        return result;
    }
}
exports.DimensionalAttribute = DimensionalAttribute;
/**
 * @internal
 */
class DimensionalLevelAttribute extends DimensionalAttribute {
    constructor(l, expression, granularity, format, desc, sort, dataSource, composeCode, panel) {
        super(l, expression, types_js_1.MetadataTypes.DateLevel, desc, sort, dataSource, composeCode);
        this._format = format;
        this.granularity = granularity;
        // panel is not needed in most cases, this is to support break by columns functionality
        if (panel === 'columns') {
            this.panel = panel;
        }
    }
    /**
     * gets the element's ID
     */
    get id() {
        let id = `${this.expression}`;
        const { level = '', dateTimeLevel = '', bucket } = this.translateGranularityToJaql();
        id += `_${(level || dateTimeLevel).toLowerCase()}`;
        if (bucket) {
            id += `_${bucket}`;
        }
        return id;
    }
    /**
     * Gets the sort definition of this instance
     *
     * @returns The Sort definition of this instance
     */
    getSort() {
        return this._sort;
    }
    /**
     * Gets a sorted {@link LevelAttribute} with the given definition
     *
     * @param sort - Sort definition
     * @returns An instance representing the sorted {@link LevelAttribute} of this instance
     */
    sort(sort) {
        return new DimensionalLevelAttribute(this.name, this.expression, this.granularity, this._format, this.description, sort, this.dataSource, this.composeCode);
    }
    /**
     * The string formatting of this instance
     *
     * @returns string formatting
     */
    getFormat() {
        return this._format;
    }
    /**
     * Gets a formatted {@link LevelAttribute} with the given definition
     *
     * @param format - Format string
     * @returns An instance representing the formatted {@link LevelAttribute} of this instance
     */
    format(format) {
        return new DimensionalLevelAttribute(this.name, this.expression, this.granularity, format, this.description, this._sort, this.dataSource, this.composeCode);
    }
    /**
     * Gets a serializable representation of the element
     */
    serializable() {
        const result = super.serializable();
        result.granularity = this.granularity;
        if (this.getFormat() !== undefined) {
            result.format = this.getFormat();
        }
        return result;
    }
    /**
     * Gets the JAQL representation of this instance
     *
     * @param nested - defines whether the JAQL is nested within parent JAQL statement or a root JAQL element
     */
    jaql(nested) {
        const r = {
            jaql: Object.assign({ title: this.name, dim: this.expression, datatype: (0, exports.jaqlSimpleColumnType)(this.type) }, this.translateGranularityToJaql()),
        };
        if (this.panel) {
            r.panel = this.panel;
        }
        if (this._format !== undefined) {
            const levelName = r.jaql.dateTimeLevel || r.jaql.level;
            r.format = { mask: {} };
            r.format.mask[levelName] = this._format;
        }
        if (this._sort != types_js_1.Sort.None) {
            r.jaql.sort = this._sort == types_js_1.Sort.Ascending ? 'asc' : 'desc';
        }
        return nested === true ? r.jaql : r;
    }
    /**
     * Obtain the JAQL representation of the level that depends on the granularity
     */
    translateGranularityToJaql() {
        const MINUTES_LEVEL = 'minutes';
        const SECONDS_LEVEL = 'seconds';
        switch (this.granularity) {
            case types_js_1.DateLevels.Years:
            case types_js_1.DateLevels.Quarters:
            case types_js_1.DateLevels.Months:
            case types_js_1.DateLevels.Weeks:
            case types_js_1.DateLevels.Days:
                return { level: this.granularity.toLowerCase() };
            case types_js_1.DateLevels.Hours:
                return {
                    dateTimeLevel: MINUTES_LEVEL,
                    bucket: '60',
                };
            case types_js_1.DateLevels.MinutesRoundTo30:
                return {
                    dateTimeLevel: MINUTES_LEVEL,
                    bucket: '30',
                };
            case types_js_1.DateLevels.MinutesRoundTo15:
                return {
                    dateTimeLevel: MINUTES_LEVEL,
                    bucket: '15',
                };
            case types_js_1.DateLevels.Minutes:
                return {
                    dateTimeLevel: MINUTES_LEVEL,
                    bucket: '1',
                };
            case types_js_1.DateLevels.Seconds:
                return {
                    dateTimeLevel: SECONDS_LEVEL,
                    bucket: '0',
                };
            case types_js_1.DateLevels.AggHours:
                return {
                    level: MINUTES_LEVEL,
                    bucket: '60',
                };
            case types_js_1.DateLevels.AggMinutesRoundTo30:
                return {
                    level: MINUTES_LEVEL,
                    bucket: '30',
                };
            case types_js_1.DateLevels.AggMinutesRoundTo15:
                return {
                    level: MINUTES_LEVEL,
                    bucket: '15',
                };
            case types_js_1.DateLevels.AggMinutesRoundTo1:
                return {
                    level: MINUTES_LEVEL,
                    bucket: '1',
                };
            default:
                console.warn('Unsupported level');
                return { level: this.granularity };
        }
    }
    static translateJaqlToGranularity(json) {
        const returnUnsupported = (lvl) => {
            console.warn('Unsupported granularity', lvl);
            return lvl;
        };
        if (json.dateTimeLevel) {
            if (json.dateTimeLevel !== 'minutes' && json.dateTimeLevel !== 'seconds') {
                return returnUnsupported(json.dateTimeLevel);
            }
            switch (json.bucket) {
                case '60':
                    return types_js_1.DateLevels.Hours;
                case '30':
                    return types_js_1.DateLevels.MinutesRoundTo30;
                case '15':
                    return types_js_1.DateLevels.MinutesRoundTo15;
                case '1':
                    return types_js_1.DateLevels.Minutes;
                case '0':
                    return types_js_1.DateLevels.Seconds;
                default:
                    return returnUnsupported(json.dateTimeLevel);
            }
        }
        switch (json.level) {
            case 'years':
                return types_js_1.DateLevels.Years;
            case 'quarters':
                return types_js_1.DateLevels.Quarters;
            case 'months':
                return types_js_1.DateLevels.Months;
            case 'weeks':
                return types_js_1.DateLevels.Weeks;
            case 'days':
                return types_js_1.DateLevels.Days;
            case 'minutes':
                switch (json.bucket) {
                    case '60':
                        return types_js_1.DateLevels.AggHours;
                    case '30':
                        return types_js_1.DateLevels.AggMinutesRoundTo30;
                    case '15':
                        return types_js_1.DateLevels.AggMinutesRoundTo15;
                    case '1':
                        return types_js_1.DateLevels.AggMinutesRoundTo1;
                    default:
                        return returnUnsupported(json.level);
                }
            default:
                return returnUnsupported(json.level);
        }
    }
    static getDefaultFormatForGranularity(granularity) {
        switch (granularity) {
            case types_js_1.DateLevels.Years:
                return 'yyyy';
            case types_js_1.DateLevels.Quarters:
                return 'Q yyyy';
            case types_js_1.DateLevels.Months:
                return 'yyyy-MM';
            case types_js_1.DateLevels.Weeks:
                return 'ww yyyy';
            case types_js_1.DateLevels.Days:
                return 'yyyy-MM-dd';
            case types_js_1.DateLevels.Hours:
                // eslint-disable-next-line sonarjs/no-duplicate-string
                return 'yyyy-MM-dd HH:mm';
            case types_js_1.DateLevels.MinutesRoundTo30:
                return 'yyyy-MM-dd HH:mm';
            case types_js_1.DateLevels.MinutesRoundTo15:
                return 'yyyy-MM-dd HH:mm';
            case types_js_1.DateLevels.Minutes:
                return 'yyyy-MM-dd HH:mm';
            case types_js_1.DateLevels.Seconds:
                return 'yyyy-MM-dd HH:mm:ss';
            case types_js_1.DateLevels.AggHours:
                return 'HH:mm';
            case types_js_1.DateLevels.AggMinutesRoundTo30:
                return 'HH:mm';
            case types_js_1.DateLevels.AggMinutesRoundTo15:
                return 'HH:mm';
            case types_js_1.DateLevels.AggMinutesRoundTo1:
                return 'HH:mm';
            default:
                return '';
        }
    }
}
exports.DimensionalLevelAttribute = DimensionalLevelAttribute;
/**
 * Creates an Attribute instance from the given JSON object.
 * If the JSON object contains a granularity property, a {@link LevelAttribute} instance is created.
 *
 * This function is used in the generated data model code to create dimension attributes from an input data source.
 *
 * See also functions {@link createDimension} and {@link createDateDimension}.
 *
 * @param json - JSON object representing the attribute
 * @returns An Attribute instance
 * @group Data Model Utilities
 */
function createAttribute(json) {
    if (json.granularity) {
        return createLevel(json);
    }
    return new DimensionalAttribute(json.name || json.title, json.attribute || json.expression || json.dim, json.type, json.desc || json.description, json.sort, json.dataSource);
}
exports.createAttribute = createAttribute;
/**
 * Creates a LevelAttribute instance from the given JSON object.
 *
 * @param json - JSON object representing the level attribute
 * @internal
 */
function createLevel(json) {
    return new DimensionalLevelAttribute(json.name || json.title, json.attribute || json.expression || json.dim, json.granularity, json.format, json.desc || json.description, json.sort, json.dataSource);
}
exports.createLevel = createLevel;
/**
 * Normalize attribute name
 *
 * @param tableName - Table name (e.g., Commerce Sales)
 * @param columnName - Column name (e.g., Order Date)
 * @param dateLevel - Date level (e.g., Years)
 * @param modelName - module name (e.g., DM)
 * @return full normalized attribute name (e.g., DM.CommerceSales.OrderDate.Years)
 * @internal
 */
function normalizeAttributeName(tableName, columnName, dateLevel, modelName) {
    return ((modelName && modelName.length > 0 ? modelName + '.' : '') +
        (0, base_js_1.normalizeName)(tableName) +
        '.' +
        (0, base_js_1.normalizeName)(columnName) +
        (dateLevel && dateLevel.length > 0
            ? '.' + DimensionalLevelAttribute.translateJaqlToGranularity({ level: dateLevel })
            : ''));
}
exports.normalizeAttributeName = normalizeAttributeName;
